import { FilterButtons } from "@/components/missions/filter-buttons";
import { MissionCard } from "@/components/missions/mission-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { PlusIcon } from "lucide-react";
import { useState } from "react";
import { useActiveClub } from "../../data/clubs/hooks";
import { useMissions } from "../../data/missions/hooks";
import { MissionType } from "../../data/missions/types";
import { useAuthStore } from "../../data/useAuthStore";

export const Route = createFileRoute("/missions/")({
  component: RouteComponent,
  loader() {
    const activeClubId = useAuthStore.getState().activeClubId;

    if (!activeClubId) {
      return redirect({
        to: "/home",
      });
    }
  },
});

function RouteComponent() {
  const activeClubId = useAuthStore((store) => store.activeClubId);
  const userClub = useActiveClub();

  const { data: missions } = useMissions(activeClubId || "");
  const [filter, setFilter] = useState<MissionType | "ALL" | "FINISHED">("ALL");

  const isFinished = (finishedAt: string | null): boolean => {
    if (!finishedAt) return false;
    return new Date(finishedAt) <= new Date();
  };

  const filteredMissions = missions
    ? filter === "ALL"
      ? missions.filter((mission) => !isFinished(mission.finishedAt))
      : filter === "FINISHED"
        ? missions.filter((mission) => isFinished(mission.finishedAt))
        : missions.filter(
            (mission) =>
              mission.type === filter && !isFinished(mission.finishedAt)
          )
    : [];

  return (
    <div className="w-full h-full p-4 bg-gradient-to-br from-[#2b7fff]/10 to-[#fe9a00]/10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#2b7fff] to-[#fe9a00] bg-clip-text text-transparent">
            Missions
          </h1>
          <p className="text-gray-600">
            Track your fitness missions and create new challenges
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 items-center">
          <FilterButtons filter={filter} setFilter={setFilter} />

          <Link to="/missions/create" disabled={!userClub?.isAdmin}>
            <Button
              className="bg-gradient-to-r from-[#2b7fff] to-[#4e93ff] hover:from-[#1a6eee] hover:to-[#3d82ee] text-white shadow-md"
              disabled={!userClub?.isAdmin}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create New
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredMissions.map((mission) => (
          <MissionCard key={mission.id} mission={mission} />
        ))}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          whileHover={{ y: -5, transition: { duration: 0.2 } }}
        >
          {userClub?.isAdmin ? (
            <Link to="/missions/create">
              <Card className="h-full border-dashed border-2 border-gray-200 bg-white/50 backdrop-blur-sm hover:border-[#2b7fff] hover:bg-white/80 transition-all flex flex-col gap-2 justify-center items-center p-4 cursor-pointer">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mt-2">
                  <PlusIcon className="h-8 w-8 text-[#2b7fff]" />
                </div>

                <h3 className="text-lg font-medium text-gray-800">
                  Create New Mission
                </h3>

                <p className="text-gray-500 text-center text-sm mb-2">
                  We'll help you generate the perfect mission for your goals!
                </p>

                <Badge className="bg-gradient-to-r from-[#2b7fff] to-[#4e93ff] hover:from-[#1a6eee] hover:to-[#3d82ee]">
                  Quick Start
                </Badge>
              </Card>
            </Link>
          ) : null}
        </motion.div>
      </div>
    </div>
  );
}
